// Brand kit guide — the Library's Brand tab: the organization's logos, photos,
// graphics, icons, charts, fonts, own categories and color sets (BrandPanel).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const brandKitGuide = {
  id: "brand-kit",
  icon: "palette",
  keywords: [
    "brand",
    "marka",
    "brand kit",
    "marka kiti",
    "logo",
    "main logo",
    "ana logo",
    "brand color",
    "marka rengi",
    "color set",
    "renk seti",
    "palette",
    "palet",
    "brand font",
    "yazı tipi",
    "google font",
    "category",
    "kategori",
    "all scenes",
    "tüm sahneler",
    "position",
    "konum",
    "icons",
    "simgeler",
    "charts",
    "grafikler",
    "kit de marca",
    "logotipo",
    "logo principal",
    "color de marca",
    "conjunto de colores",
    "paleta",
    "fuente de marca",
    "fuente de google",
    "categoría",
    "todas las escenas",
    "posición",
    "iconos",
    "diagramas",
  ],
  content: {
    en: {
      feature: "Library",
      title: "Brand kit",
      sections: [
        {
          id: "overview",
          title: "Opening the Brand tab",
          videoUrl: null,
          text:
            "Open the **Library** and pick the **Brand** tab. It shows your organization's shared brand library — the same for every training, not tied to one course.\n\n" +
            "The panel opens on a grid of category cards, each with its file count: **Logos**, **Photos**, **Graphics**, **Icons**, **Charts**, **Fonts** and **Colors**, followed by any categories your organization created. Click a card to open it and use the back arrow to return.\n\n" +
            "- **Search** at the top finds files by name. On the category cards it searches every category at once; inside a category it only filters that category.\n" +
            "- Every organization member can upload files and edit color sets. Deleting files, creating, renaming or deleting categories, deleting color sets and choosing the main logo are for organization owners and admins.",
        },
        {
          id: "place-assets",
          title: "Placing brand assets on a scene",
          videoUrl: null,
          text:
            "Click a logo, photo, graphic, icon, chart or custom-category image to add it to the current scene. It lands in the **top-right corner**, sized to fit, and stays visible for the whole scene. Brand assets always stay on the top layer, above the scene's other elements.\n\n" +
            "To use the same spot everywhere, place the asset, move and resize it where you want, then right-click it on the canvas and choose **Update position on all scenes**. It is copied with the same position and size onto every other scene; a scene that already has it just has its copy moved. The whole change is a single undo step.\n\n" +
            "On an interactive scene the image is added as an element of the scenario instead, so it has no all-scenes option.\n\n" +
            "Hover a file for its actions: **Preview**, **Download**, and **Delete** (owners and admins, after a confirmation).",
        },
        {
          id: "upload",
          title: "Uploading and categories",
          videoUrl: null,
          text:
            "Inside a category, drop files on the upload area or click it to choose them. Files wait in a queue with a preview and size; click **Upload file** to send them all or **Clear** to empty the queue. If one file fails it stays in the queue with its error and the rest still upload.\n\n" +
            "- Logos, icons and charts take PNG, JPEG, WebP or SVG; photos take PNG, JPEG or WebP; graphics and your own categories also take GIF.\n" +
            "- In **Logos**, owners and admins can tick **Set as main logo** on a queued file. The organization's main logo shows a **Primary** badge.\n" +
            "- Owners and admins can add their own folders with the **New category** card at the end of the grid. Use the pencil (**Rename**) and trash (**Delete category**) icons on a custom category to change it — deleting a category also deletes the files in it.",
        },
        {
          id: "colors",
          title: "Brand colors",
          videoUrl: null,
          text:
            "The **Colors** card holds your organization's color sets. Each set has three slots: **Primary Color**, **Secondary Color** and **Accent Color**.\n\n" +
            "- Type a hex code (for example #40cfde) and press Enter, or use the picker next to it. The color is saved automatically.\n" +
            "- The × next to a slot empties it, and the pencil next to a set's name renames it.\n" +
            "- **New color set** adds an empty set, ready to be named. Owners and admins can delete a whole set.\n\n" +
            "Click a filled swatch to apply its color. It goes to the selected text box as its text color, to a selected shape as its fill, and with nothing selected it becomes the current scene's background. A short message confirms where it went; other kinds of elements can't take a brand color.",
        },
        {
          id: "fonts",
          title: "Brand fonts",
          videoUrl: null,
          text:
            "The **Fonts** category holds your organization's typefaces. You can upload font files (WOFF2, WOFF, TTF or OTF) or choose one from **Pick a Google font…**, which lists the Google fonts not already in the library.\n\n" +
            'Each font shows an "Aa" sample; **Preview** shows the full alphabet and digits. Clicking a font adds a new **Rich Text** box to the current scene, already set in that font.\n\n' +
            "Keep in mind that an **uploaded font file only renders in the editor**. Preview and exports fall back to a standard font for it. Google fonts picked from the list display everywhere.",
        },
      ],
    },
    tr: {
      feature: "Kütüphane",
      title: "Marka kiti",
      sections: [
        {
          id: "overview",
          title: "Marka sekmesini açma",
          videoUrl: null,
          text:
            "**Kütüphane**'yi açıp **Marka** sekmesini seçin. Burada kuruluşunuzun ortak marka kütüphanesi bulunur; tek bir eğitime bağlı değildir, her eğitimde aynıdır.\n\n" +
            "Panel, dosya sayılarıyla birlikte kategori kartlarını gösterir: **Logolar**, **Fotoğraflar**, **Grafik bileşenler**, **Simgeler**, **Grafikler**, **Yazı tipleri** ve **Renkler**; ardından kuruluşunuzun oluşturduğu kategoriler gelir. Bir kartı açmak için tıklayın, geri dönmek için geri okunu kullanın.\n\n" +
            "- Üstteki **Ara** alanı dosyaları adına göre bulur. Kategori kartları ekranındayken tüm kategorilerde birden arar; bir kategorinin içindeyken yalnızca o kategoriyi filtreler.\n" +
            "- Kuruluşun her üyesi dosya yükleyebilir ve renk setlerini düzenleyebilir. Dosya silme, kategori oluşturma, yeniden adlandırma veya silme, renk seti silme ve ana logoyu seçme yalnızca kuruluş sahipleri ve yöneticileri içindir.",
        },
        {
          id: "place-assets",
          title: "Marka öğelerini sahneye yerleştirme",
          videoUrl: null,
          text:
            "Bir logoya, fotoğrafa, grafiğe, simgeye veya özel kategorideki bir görsele tıklayarak geçerli sahneye ekleyin. Öğe uygun bir boyutla **sağ üst köşeye** yerleşir ve sahne boyunca görünür kalır. Marka öğeleri her zaman en üst katmanda, sahnedeki diğer öğelerin üzerinde durur.\n\n" +
            "Her sahnede aynı konumu kullanmak için öğeyi yerleştirin, istediğiniz yere taşıyıp boyutlandırın, sonra kanvasta sağ tıklayıp **Tüm sahnelerde konumu güncelle**'yi seçin. Öğe aynı konum ve boyutla diğer tüm sahnelere kopyalanır; öğeyi zaten içeren bir sahnede yalnızca mevcut kopya taşınır. Değişikliğin tamamı tek adımda geri alınabilir.\n\n" +
            "Etkileşimli bir sahnede görsel, senaryonun bir öğesi olarak eklenir; bu yüzden tüm sahnelere uygulama seçeneği yoktur.\n\n" +
            "Bir dosyanın üzerine gelince işlemleri görünür: **Ön izleme**, **İndir** ve onay sonrası **Sil** (sahipler ve yöneticiler).",
        },
        {
          id: "upload",
          title: "Yükleme ve kategoriler",
          videoUrl: null,
          text:
            "Bir kategorinin içinde dosyaları yükleme alanına bırakın veya tıklayıp seçin. Dosyalar önizleme ve boyut bilgisiyle bir kuyrukta bekler; hepsini göndermek için **Yükle**'ye, kuyruğu boşaltmak için **Temizle**'ye tıklayın. Bir dosya başarısız olursa hatasıyla kuyrukta kalır, diğerleri yüklenmeye devam eder.\n\n" +
            "- Logolar, simgeler ve grafikler PNG, JPEG, WebP veya SVG kabul eder; fotoğraflar PNG, JPEG veya WebP; grafik bileşenler ve kendi kategorileriniz ayrıca GIF de kabul eder.\n" +
            "- **Logolar** içinde sahipler ve yöneticiler kuyruktaki bir dosya için **Ana logo yap** kutusunu işaretleyebilir. Kuruluşun ana logosunda **Ana** rozeti görünür.\n" +
            "- Sahipler ve yöneticiler, kartların sonundaki **Yeni kategori** ile kendi klasörlerini ekleyebilir. Özel bir kategorideki kalem (**Yeniden adlandır**) ve çöp kutusu (**Kategoriyi sil**) simgeleriyle kategoriyi düzenleyebilirsiniz; bir kategoriyi silmek içindeki dosyaları da siler.",
        },
        {
          id: "colors",
          title: "Marka renkleri",
          videoUrl: null,
          text:
            "**Renkler** kartında kuruluşunuzun renk setleri bulunur. Her sette üç alan vardır: **Birincil Renk**, **İkincil Renk** ve **Vurgu Rengi**.\n\n" +
            "- Bir hex kodu yazıp (ör. #40cfde) Enter'a basın veya yanındaki renk seçiciyi kullanın. Renk otomatik olarak kaydedilir.\n" +
            "- Bir alanın yanındaki × o alanı boşaltır; setin adının yanındaki kalem simgesi seti yeniden adlandırır.\n" +
            "- **Yeni renk seti** adlandırmaya hazır boş bir set ekler. Sahipler ve yöneticiler bir setin tamamını silebilir.\n\n" +
            "Rengi uygulamak için dolu bir renk kutucuğuna tıklayın. Renk, seçili metin kutusuna metin rengi olarak, seçili şekle dolgu rengi olarak uygulanır; hiçbir şey seçili değilse geçerli sahnenin arka planı olur. Rengin nereye uygulandığını kısa bir mesaj bildirir; diğer öğe türlerine marka rengi uygulanamaz.",
        },
        {
          id: "fonts",
          title: "Marka yazı tipleri",
          videoUrl: null,
          text:
            "**Yazı tipleri** kategorisinde kuruluşunuzun yazı tipleri bulunur. Yazı tipi dosyası (WOFF2, WOFF, TTF veya OTF) yükleyebilir ya da kütüphanede henüz olmayan Google fontlarını listeleyen **Google fontu seç…** menüsünden birini seçebilirsiniz.\n\n" +
            'Her yazı tipi bir "Aa" örneğiyle görünür; **Ön izleme** tüm alfabeyi ve rakamları gösterir. Bir yazı tipine tıklamak, geçerli sahneye o yazı tipiyle ayarlanmış yeni bir **Zengin Metin** kutusu ekler.\n\n' +
            "Unutmayın: **yüklenen yazı tipi dosyaları yalnızca editörde görünür**. Ön izleme ve dışa aktarımlarda bunların yerine standart bir yazı tipi kullanılır. Listeden seçilen Google fontları her yerde görünür.",
        },
      ],
    },
    es: {
      feature: "Biblioteca",
      title: "Kit de marca",
      sections: [
        {
          id: "overview",
          title: "Abrir la pestaña Marca",
          videoUrl: null,
          text:
            "Abre la **Biblioteca** y elige la pestaña **Marca**. Muestra la biblioteca de marca compartida de tu organización: es la misma en todas las formaciones y no está ligada a un solo curso.\n\n" +
            "El panel se abre con una cuadrícula de tarjetas de categoría, cada una con su número de archivos: **Logotipos**, **Fotos**, **Gráficos**, **Iconos**, **Diagramas**, **Fuentes** y **Colores**, seguidas de las categorías que haya creado tu organización. Haz clic en una tarjeta para abrirla y usa la flecha atrás para volver.\n\n" +
            "- **Buscar**, en la parte superior, encuentra archivos por su nombre. En la vista de tarjetas busca en todas las categorías a la vez; dentro de una categoría solo filtra esa categoría.\n" +
            "- Todos los miembros de la organización pueden subir archivos y editar los conjuntos de colores. Eliminar archivos, crear, renombrar o eliminar categorías, eliminar conjuntos de colores y elegir el logotipo principal está reservado a los propietarios y administradores de la organización.",
        },
        {
          id: "place-assets",
          title: "Colocar recursos de marca en una escena",
          videoUrl: null,
          text:
            "Haz clic en un logotipo, una foto, un gráfico, un icono, un diagrama o una imagen de una categoría personalizada para añadirlo a la escena actual. Se coloca en la **esquina superior derecha**, con un tamaño ajustado, y permanece visible durante toda la escena. Los recursos de marca siempre quedan en la capa superior, por encima del resto de elementos de la escena.\n\n" +
            "Para usar la misma posición en todas partes, coloca el recurso, muévelo y cámbiale el tamaño a tu gusto, y después haz clic derecho sobre él en el lienzo y elige **Actualizar posición en todas las escenas**. Se copia con la misma posición y tamaño en todas las demás escenas; en una escena que ya lo tiene, solo se mueve su copia. Todo el cambio se deshace en un solo paso.\n\n" +
            "En una escena interactiva, la imagen se añade como un elemento del escenario, por lo que no tiene la opción de todas las escenas.\n\n" +
            "Pasa el cursor sobre un archivo para ver sus acciones: **Vista Previa**, **Descargar** y **Eliminar** (propietarios y administradores, tras una confirmación).",
        },
        {
          id: "upload",
          title: "Subida de archivos y categorías",
          videoUrl: null,
          text:
            "Dentro de una categoría, suelta los archivos en la zona de carga o haz clic en ella para elegirlos. Los archivos esperan en una cola con una vista previa y su tamaño; haz clic en **Subir Fichero** para enviarlos todos o en **Borrar** para vaciar la cola. Si un archivo falla, se queda en la cola con su error y los demás se suben igualmente.\n\n" +
            "- Logotipos, iconos y diagramas admiten PNG, JPEG, WebP o SVG; las fotos, PNG, JPEG o WebP; los gráficos y tus propias categorías admiten además GIF.\n" +
            "- En **Logotipos**, los propietarios y administradores pueden marcar **Usar como logo principal** en un archivo de la cola. El logotipo principal de la organización muestra la insignia **Principal**.\n" +
            "- Los propietarios y administradores pueden añadir sus propias carpetas con la tarjeta **Nueva categoría** al final de la cuadrícula. Usa los iconos del lápiz (**Cambiar nombre**) y de la papelera (**Eliminar categoría**) de una categoría personalizada para modificarla; al eliminar una categoría también se eliminan sus archivos.",
        },
        {
          id: "colors",
          title: "Colores de marca",
          videoUrl: null,
          text:
            "La tarjeta **Colores** contiene los conjuntos de colores de tu organización. Cada conjunto tiene tres casillas: **Color primario**, **Color secundario** y **Color de acento**.\n\n" +
            "- Escribe un código hex (por ejemplo, #40cfde) y pulsa Enter, o usa el selector de color que hay al lado. El color se guarda automáticamente.\n" +
            "- La × junto a una casilla la vacía, y el lápiz junto al nombre de un conjunto le cambia el nombre.\n" +
            "- **Nuevo conjunto de colores** añade un conjunto vacío, listo para ponerle nombre. Los propietarios y administradores pueden eliminar un conjunto entero.\n\n" +
            "Haz clic en una muestra con color para aplicarlo. Se aplica al cuadro de texto seleccionado como color del texto, a una forma seleccionada como relleno y, si no hay nada seleccionado, pasa a ser el fondo de la escena actual. Un breve mensaje confirma dónde se ha aplicado; los demás tipos de elementos no admiten un color de marca.",
        },
        {
          id: "fonts",
          title: "Fuentes de marca",
          videoUrl: null,
          text:
            "La categoría **Fuentes** contiene las tipografías de tu organización. Puedes subir archivos de fuente (WOFF2, WOFF, TTF u OTF) o elegir una en **Elige una fuente de Google…**, que muestra las fuentes de Google que aún no están en la biblioteca.\n\n" +
            'Cada fuente se presenta con una muestra "Aa"; **Vista Previa** muestra el alfabeto completo y los números. Al hacer clic en una fuente se añade a la escena actual un nuevo cuadro de **Texto Enriquecido** que ya usa esa fuente.\n\n' +
            "Ten en cuenta que **un archivo de fuente subido solo se ve en el editor**. La vista previa y las exportaciones usan en su lugar una fuente estándar. Las fuentes de Google elegidas de la lista se ven en todas partes.",
        },
      ],
    },
  },
};

export default brandKitGuide;
